import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const TeamJoinForm = ({ onTeamJoined, onCancel, onSuccess, initialTeamCode = '', initialEmail = '', initialRole = '' }) => {
  const { user, joinTeam } = useAuth();
  const [teamCode, setTeamCode] = useState(initialTeamCode);
  const [relationshipType, setRelationshipType] = useState(initialRole);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get available role options based on user's account role
  const getAvailableRoles = () => {
    const roleOptions = {
      'admin': [
        { value: 'coach', label: 'Coach' },
        { value: 'assistant_coach', label: 'Assistant Coach' },
        { value: 'parent', label: 'Parent' },
        { value: 'family', label: 'Family Member' },
        { value: 'spectator', label: 'Spectator' }
      ],
      'org_admin': [
        { value: 'coach', label: 'Coach' },
        { value: 'assistant_coach', label: 'Assistant Coach' },
        { value: 'parent', label: 'Parent' },
        { value: 'family', label: 'Family Member' },
        { value: 'spectator', label: 'Spectator' }
      ],
      'coach': [
        { value: 'coach', label: 'Coach' },
        { value: 'assistant_coach', label: 'Assistant Coach' },
        { value: 'parent', label: 'Parent' },
        { value: 'family', label: 'Family Member' },
        { value: 'spectator', label: 'Spectator' }
      ],
      'parent': [
        { value: 'parent', label: 'Parent' },
        { value: 'family', label: 'Family Member' }
      ],
      'player': [
        { value: 'player', label: 'Player' }
      ]
    };

    return roleOptions[user?.role] || [{ value: 'spectator', label: 'Spectator' }];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const requestData = {
        teamCode: teamCode.trim().toUpperCase()
      };

      // Only include relationshipType if user selected one
      if (relationshipType) {
        requestData.relationshipType = relationshipType;
      }

      const response = await axios.post('/api/teams/join', requestData, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      const roleLabel = getAvailableRoles().find(r => r.value === response.data.relationshipType)?.label || response.data.relationshipType;
      toast.success(`Successfully joined ${response.data.team.name} as ${roleLabel}!`);
      
      // Update the user's team status in context
      joinTeam(response.data.team.id);
      
      if (onTeamJoined) {
        onTeamJoined(response.data.team);
      }
      if (onSuccess) {
        onSuccess(response.data.team);
      }
    } catch (error) {
      console.error('Error joining team:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to join team');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Join a Team</h2>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="teamCode" className="block text-sm font-medium text-gray-700 mb-2">
            Team Join Code
          </label>
          <input
            type="text"
            id="teamCode"
            value={teamCode}
            onChange={(e) => setTeamCode(e.target.value.toUpperCase())}
            required
            maxLength={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono tracking-wider"
            placeholder="Enter team code"
          />
          <p className="text-sm text-gray-500 mt-2">
            Enter the 6-character team code provided by your coach or organization admin.
          </p>
        </div>

        {/* Role Selection - only show if user has multiple options */}
        {getAvailableRoles().length > 1 && (
          <div>
            <label htmlFor="relationshipType" className="block text-sm font-medium text-gray-700 mb-2">
              Join as
            </label>
            <select
              id="relationshipType"
              value={relationshipType}
              onChange={(e) => setRelationshipType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose your role (optional)</option>
              {getAvailableRoles().map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-2">
              {user?.role === 'parent' 
                ? "Choose 'Parent' if you're a parent/guardian, or 'Family Member' for other family relationships."
                : "Select how you'd like to join this team. If left blank, we'll use your account role."
              }
            </p>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-800">How to get a team code:</h4>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>• Ask your coach or team administrator</li>
                <li>• Check team communications or announcements</li>
                <li>• Contact your organization admin</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !teamCode.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Joining...' : 'Join Team'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamJoinForm;
